import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import css from './upcoming-interview.css';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardActions,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {useCopyToClipboard} from '@spaced-out/ui-design-system/lib/hooks/useCopyToClipboard';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {
  SemanticIcon,
  Icon,
} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  Toast,
  ToastTitle,
  toastApi,
  TOAST_SEMANTIC,
  type ToastSemanticType,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  ButtonTextMedium,
  SubTitleLarge,
  SubTitleExtraSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {useInflight} from 'src/hooks/redux';
import {
  getApplicantInformation,
  getInterviewSchedules,
} from 'src/action-creators/crm';
import {
  Panel,
  PanelBody,
  PanelHeader,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'src/rerouter/index';
// Flow(untyped-import)
import {
  formatMomentDateWithFormat,
  formatMomentTimeWithFormat,
} from 'src/utils/date-time';
import SubmitFeedbacks from 'src/components/crm/jobs/job-info/overview/feedbacks/submit-feedbacks';
import useScrollLoader from 'src/hooks/useScrollLoader';


type InterviewData = {
  id: string;
  subject: string;
  interviewDateAsString: string;
  conferenceLink: string;
  candidateName: string;
  jobTitle: string;
  stepName: string;
  startDate: Date;
  endDate: Date;
  stepCandidateId: string;
  candidateId: number;
  jobId: string;
};

type Props = {
  interviewData: Array<InterviewData>;
  isInterviewsLoading: boolean;
  jobId?: string;
  from?: string;
};

type InterviewsProps = {
  interviewData: InterviewData;
  setCandidateId: (id: number) => void;
  from?: string;
};

type UpcomingInterviewPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  interviewData: Array<InterviewData>;
  isInterviewsLoading: boolean;
  jobId?: string;
};

export const UpcomingInterview = ({
  interviewData,
  isInterviewsLoading,
  jobId,
  from,
}: Props) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [isPanelOpen, setIsPanelOpen] = React.useState(false);
  const [candidateId, setCandidateId] = React.useState<any>(null);
  const dispatch = useDispatch();

  const isApplicantInformationLoading = useInflight(
    getApplicantInformation(candidateId),
  );
  const isInterviewSchedulesLoading = useInflight(
    getInterviewSchedules('PENDING', jobId),
  );
  React.useEffect(() => {
    if (candidateId) {
      dispatch(getApplicantInformation(candidateId));
    }
  }, [candidateId]);
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="video"
              semantic="neutral"
              type="solid"
              size="small"
            />
            {t('Upcoming_Interviews', 'Upcoming Interviews')}
            {interviewData.length > 0 && (
              <Badge text={String(interviewData.length)} fill="blue" />
            )}
          </CardTitle>
          <CardActions>
            {interviewData.length > 0 ? (
              <Button
                onClick={() => setIsPanelOpen(true)}
                children={t('View_all', 'View all')}
                type="ghost"
                size="small"
              />
            ) : null}
          </CardActions>
        </CardHeader>
        {isInterviewsLoading ? (
          <div className={css.loaderContainer}>
            <CircularLoader size="large" />
          </div>
        ) : (
          <CardContent className={css.cardContent}>
            {interviewData.map((interview) => (
              <Interviews
                key={interview.id}
                interviewData={interview}
                setCandidateId={setCandidateId}
                from={from}
              />
            ))}

            {!interviewData.length && (
              <EmptyState
                description={`${t(
                  'Your_schedule_is_clear_for_now',
                  'Your schedule is clear for now',
                )}!`}
                imageVariant="calendar"
              />
            )}
          </CardContent>
        )}
      </Card>

      <UpcomingInterviewPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        interviewData={interviewData}
        isInterviewsLoading={isInterviewsLoading}
        jobId={jobId}
      />
    </>
  );
};

const Interviews = ({
  interviewData,
  setCandidateId,
  from = 'DASHBOARD',
}: InterviewsProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const history = useHistory();
  const {
    isReviewPanelOpen,
    stepCandidateId,
    candidateId,
    jobId,
    feedbackTabId,
    type,
  } = history.location.query;
  const [candidateStepId, setCandidateStepId] = React.useState('');
  const [copiedText, copy] = useCopyToClipboard();
  // @ts-expect-error - TS2345 - Argument of type 'Date' is not assignable to parameter of type 'string'.
  const date = formatMomentDateWithFormat(interviewData.startDate);
  const time = `${formatMomentTimeWithFormat(
    // @ts-expect-error - TS2345 - Argument of type 'Date' is not assignable to parameter of type 'string'.
    interviewData.startDate,
    // @ts-expect-error - TS2345 - Argument of type 'Date' is not assignable to parameter of type 'string'.
  )} - ${formatMomentTimeWithFormat(interviewData.endDate)} ${t('IST', 'IST')}`;

  const interviewDate = () => {
    const today = new Date().toLocaleDateString();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowString = tomorrow.toLocaleDateString();

    if (date === today) {
      return 'Today';
    } else if (date === tomorrowString) {
      return 'Tomorrow';
    }
    return date;
  };
  const timeSlot = time;

  const interviewDetails = [
    {
      text: `${t('Candidate', 'Candidate')}: ${
        interviewData.candidateName ?? ''
      }`,
      icon: 'user',
    },
    {
      text: `${t('Job', 'Job')}: ${interviewData.jobTitle ?? ''}`,
      icon: 'briefcase',
    },
    {
      text: interviewData.stepName ?? '',
      icon: 'compass',
    },
  ];
  const showToastMessage = (message: string, type: ToastSemanticType) => {
    const toastJsx = (
      <Toast semantic={type}>
        <ToastTitle>{message} </ToastTitle>
      </Toast>
    );
    toastApi.show(toastJsx, {
      autoClose: true,
    });
  };
  const handleCopyInterviewLinkToClipboard = () => {
    copy(interviewData.conferenceLink)
      .then((success) => {
        if (success) {
          showToastMessage(
            t('Link_copied_successfully', 'Link copied successfully'),
            TOAST_SEMANTIC.success,
          );
        } else {
          showToastMessage(
            t('Something_went_wrong', 'Something went wrong'),
            TOAST_SEMANTIC.danger,
          );
        }
      })
      .catch((_error) => {
        showToastMessage(
          t('Something_went_wrong', 'Something went wrong'),
          TOAST_SEMANTIC.danger,
        );
      });
  };

  return (
    <>
      {isReviewPanelOpen === 'true' && (
        <SubmitFeedbacks
          isOpen={isReviewPanelOpen === 'true'}
          handleClose={() => {
            if (from === 'DASHBOARD') {
              history.push(`/dashboard`);
            } else if (from === 'JOB_OVERVIEW' && jobId) {
              history.push(`/crm/job/${jobId}/info/overview`);
            }
          }}
          candidateStepId={interviewData.stepCandidateId}
          candidateId={interviewData.candidateId}
          jobId={interviewData.jobId}
          type="interview"
          from={from}
        />
      )}
      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <div className={css.cardHeader}>
          <ButtonTextMedium>
            <TruncatedTextWithTooltip
              tooltip={{bodyMaxLines: 6}}
            >{`${interviewData.subject}`}</TruncatedTextWithTooltip>
          </ButtonTextMedium>
          <CardActions>
            {interviewData.conferenceLink ? (
              <Button
                onClick={() =>
                  window.open(interviewData.conferenceLink, '_blank')
                }
                children={t('Join', 'Join')}
                type="tertiary"
                size="small"
                iconLeftName="video"
              />
            ) : null}
            <Tooltip title={t('Add_Feedback', 'Add Feedback')}>
              <Button
                onClick={() => {
                  const params = new URLSearchParams(window.location.search);
                  params.set('stepCandidateId', interviewData.stepCandidateId);
                  params.set(
                    'candidateId',
                    interviewData.candidateId.toString(),
                  );
                  params.set('jobId', interviewData.jobId);
                  params.set('feedbackTabId', 'interviewFeedbacks');
                  params.set('type', 'interview');
                  params.set('isReviewPanelOpen', 'true');
                  if (from === 'DASHBOARD') {
                    history.push(`/dashboard/feedbacks?${params.toString()}`);
                  } else if (interviewData.jobId) {
                    history.push(
                      `/crm/job/${
                        interviewData.jobId
                      }/info/overview?${params.toString()}`,
                    );
                  }
                }}
                type="tertiary"
                size="small"
                iconRightName="file-lines"
              />
            </Tooltip>
            {interviewData.conferenceLink ? (
              <Tooltip title={t('Copy_Interview_Link', 'Copy Interview Link')}>
                <Button
                  onClick={handleCopyInterviewLinkToClipboard}
                  type="tertiary"
                  size="small"
                  iconRightName="copy"
                />
              </Tooltip>
            ) : null}
          </CardActions>
        </div>

        <CardContent className={css.cardContent}>
          <Card
            classNames={{
              wrapper: css.innerCard,
            }}
            paddingTop="small"
            paddingBottom="small"
          >
            <div className={css.innerCardContent}>
              <div className={css.item}>
                <Icon name="calendar" size="small" />
                <SubTitleExtraSmall>{interviewDate()}</SubTitleExtraSmall>
              </div>
              <div className={css.item}>
                <Icon name="clock" size="small" />
                <SubTitleExtraSmall>{timeSlot}</SubTitleExtraSmall>
              </div>
            </div>
            <div className={css.innerCardContent}>
              {interviewDetails.map(({text, icon}) => (
                <div className={css.item}>
                  <Icon name={icon} size="small" />
                  <SubTitleExtraSmall>{text}</SubTitleExtraSmall>
                </div>
              ))}
            </div>
          </Card>
        </CardContent>
      </Card>
    </>
  );
};

const UpcomingInterviewPanel = ({
  isOpen,
  onClose,
  interviewData,
  isInterviewsLoading,
  jobId,
}: UpcomingInterviewPanelProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [candidateId, setCandidateId] = React.useState('');
  const [paginationState, setPaginationState] = React.useState({
    pageNumber: 0,
    hasMorePages: true,
  });
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  const [accumulatedInterviews, setAccumulatedInterviews] = React.useState([]);
  const dispatch = useDispatch();

  const isInterviewSchedulesLoading = useInflight(
    getInterviewSchedules('PENDING', jobId),
  );

  React.useEffect(() => {
    if (isOpen && interviewData) {
      // @ts-expect-error - TS2345 - Argument of type 'InterviewData[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
      setAccumulatedInterviews(interviewData);
    }
  }, [isOpen]);

  const onLoadMore = React.useCallback(() => {
    if (isFetchingMore || !paginationState.hasMorePages) {
      return;
    }

    setIsFetchingMore(true);
    const nextPage = paginationState.pageNumber + 1;

    dispatch(getInterviewSchedules('PENDING', jobId, nextPage))
      // @ts-expect-error - TS2339 - Property 'then' does not exist on type 'ThunkAction<void>'. | TS7006 - Parameter 'result' implicitly has an 'any' type.
      .then((result) => result.payload || result)
      .then((response) => {
        setPaginationState({
          pageNumber: nextPage,
          hasMorePages: !response?.last,
        });
        setIsFetchingMore(false);

        if (response?.content) {
          // @ts-expect-error - TS2345 - Argument of type '(prev: never[]) => any[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
          setAccumulatedInterviews((prev) => [...prev, ...response.content]);
        }
      })
      .catch((error) => {
        setIsFetchingMore(false);
        console.error('Error loading more interviews:', error);
      });
  }, [dispatch, jobId, isFetchingMore, paginationState.hasMorePages]);

  const [containerRef, loaderRef, handleMovement] = useScrollLoader({
    loading: isFetchingMore,
    done: !paginationState.hasMorePages,
    onLoadMore,
  });

  return (
    <Panel
      anchor="right"
      initialFocus={1}
      onClose={onClose}
      isOpen={isOpen}
      size="large"
      tapOutsideToClose
      hideBackdrop={false}
    >
      <PanelHeader onCloseButtonClick={onClose}>
        <SubTitleLarge>
          {t('Upcoming_Interviews', 'Upcoming Interviews')}
        </SubTitleLarge>
      </PanelHeader>

      {isInterviewsLoading || isInterviewSchedulesLoading ? (
        <div className={css.loaderContainer}>
          <CircularLoader size="large" />
        </div>
      ) : (
        <PanelBody className={css.panelBody}>
          <div
            className={css.panelBody}
            // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            ref={containerRef}
            onScroll={handleMovement}
          >
            {accumulatedInterviews.map((interview) => (
              <Interviews
                // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                key={interview.id}
                interviewData={interview}
                // @ts-expect-error - TS2322 - Type 'Dispatch<SetStateAction<string>>' is not assignable to type '(id: number) => void'.
                setCandidateId={setCandidateId}
              />
            ))}

            {!accumulatedInterviews.length ? (
              <EmptyState
                description={`${t(
                  'Your_schedule_is_clear_for_now',
                  'Your schedule is clear for now',
                )}!`}
                imageVariant="calendar"
              />
            ) : !paginationState.hasMorePages ? null : (
              // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
              <div ref={loaderRef} className={css.loadingMoreContainer}>
                {isFetchingMore && <CircularLoader size="small" />}
              </div>
            )}
          </div>
        </PanelBody>
      )}
    </Panel>
  );
};
